import React from "react";

import {
  ChartLineUpIcon,
  CirclesThreeIcon,
  CloudIcon,
  CodeIcon,
  CubeIcon,
  Files2Icon,
  GearIcon,
  NotificationsIcon,
  PlugsConnectedIcon,
  PresentationChartIcon,
  type V2SidebarNavItem,
} from "@stacklet/ui";

import { useHasScopePermission, useStickyState } from "app/hooks";

const rotatedPlugsIcon =
  (deg: number) =>
  ({ className }: { className?: string }) =>
    React.createElement(PlugsConnectedIcon, {
      className,
      style: { transform: `rotate(${deg}deg)` },
    });

const PlugsConnectedRotatedNegIcon = rotatedPlugsIcon(-45);

import type { useConfig } from "app/contexts";

type NavItem = V2SidebarNavItem;

interface Dashboard {
  label: string;
  Icon?: V2SidebarNavItem["Icon"];
  id: string;
  key: string;
  target: string;
}

export function useNavItems(
  enabledDashboards: Dashboard[],
  config: ReturnType<typeof useConfig>,
) {
  const hasPermission = useHasScopePermission();
  const isAdmin = hasPermission("system", "EDIT");

  // Persist whether the Reports section is expanded across reloads.
  const [reportsExpanded, setReportsExpanded] = useStickyState<boolean>(
    true,
    "sidebar:reports-expanded",
  );

  const hiddenDashboardKeys = config?.hidden_dashboard_keys || [];
  const shouldShowCloudDashboard = !hiddenDashboardKeys.includes("cloud");

  // Footer group: notifications, settings (both admin-only) and the API link.
  // The first item carries `startsGroup` so it is separated from the main nav.
  const footerNavItems: NavItem[] = [
    ...(isAdmin
      ? [
          {
            label: "Notifications",
            id: "notifications",
            Icon: NotificationsIcon,
            target: "/notifications/overview",
            activePaths: ["/notifications"],
          },
          {
            label: "Settings",
            id: "settings",
            Icon: GearIcon,
            target: "/settings/access-control/users",
            activePaths: ["/settings"],
          },
        ]
      : []),
    {
      label: "Stacklet API",
      Icon: CodeIcon,
      target: "/api",
      isExternalLink: false,
      id: "gql-api",
    },
  ];
  footerNavItems[0] = { ...footerNavItems[0], startsGroup: true };

  const navItems: NavItem[] = [
    {
      label: "Resources",
      Icon: CubeIcon,
      id: "resources",
      target: "/cloudops/resources",
      activePaths: ["/cloudops"],
      startsGroup: true,
    },
    {
      label: "Accounts",
      Icon: CirclesThreeIcon,
      target: "/admin/accounts",
      id: "admin-accounts",
      activePaths: ["/admin/account-groups"],
    },
    {
      label: "Bindings",
      Icon: PlugsConnectedRotatedNegIcon,
      id: "admin-bindings",
      target: "/admin/bindings",
    },
    {
      label: "Policies",
      Icon: Files2Icon,
      id: "admin-policies",
      target: "/admin/policies",
      activePaths: ["/admin/policy-collections", "/admin/repositories"],
    },
    {
      label: "Reports",
      id: "reports",
      Icon: PresentationChartIcon,
      defaultExpanded: reportsExpanded,
      onExpandedChange: setReportsExpanded,
      children: [
        ...(shouldShowCloudDashboard
          ? [
              {
                label: "Clouds",
                Icon: CloudIcon,
                id: "clouds",
                target: "/dashboards/cloud",
              },
            ]
          : []),
        {
          label: "Stacklet Activity",
          Icon: ChartLineUpIcon,
          target: "/activity",
          id: "activity",
        },
        ...enabledDashboards,
      ],
    },
    ...footerNavItems,
  ];

  return navItems;
}
